/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    ACTION_VIEW,
    DialogWizardActions,
    DialogWizardContentHeader,
    DialogWizardLayout,
    DialogWizardStepContent,
    MessageBox,
    TabList,
    useModal,
} from '@veeam/components';

import { questionIconSrc } from '../../icons';
import { WizardStateStore } from '../stores';
import { WizardDataContext } from './contexts';

import type { WizardProps } from './interfaces';

export interface DialogWizardProps<T> extends WizardProps<T>{
    closeMessageOkButtonHide?: boolean;
}

export const DialogWizard = observer(<T,>(props: DialogWizardProps<T>) => {

    const [wizardStateStore] = useState(() => new WizardStateStore<T>({ ...props }));
    const hideWizardActon = () => {
        props.deactivate();
        props.onClose && props.onClose();
    };

    // mobx force update hack
    // eslint-disable-next-line no-unused-vars
    const { currentStepId, reloadVar } = wizardStateStore;

    const [dialog, dialogActions] = useModal({
        render: () => (
            <MessageBox
                iconSrc={questionIconSrc}
                header={wizardStateStore.header}
                onRequestClose={() => dialogActions.deactivate()}
                actions={[
                    !props.closeMessageOkButtonHide && {
                        text: props.closeMessageOkButtonText,
                        onClick() {
                            dialogActions.deactivate();
                            hideWizardActon();
                        },
                    },
                    {
                        text: props.closeMessageCancelButtonText,
                        view: props.closeMessageOkButtonHide ? ACTION_VIEW.primary : ACTION_VIEW.secondary,
                        onClick() {
                            dialogActions.deactivate();
                        },
                    },
                ].filter(Boolean)}
            >
                {props.closeMessageText}
            </MessageBox>
        ),
    });

    return (
        <WizardDataContext.Provider value={wizardStateStore}>
            <DialogWizardLayout
                tabs={(
                    <TabList
                        items={wizardStateStore.tabsConfig}
                    />
                )}
                dialogHeader={wizardStateStore.header}
                content={(
                    <>
                        <DialogWizardContentHeader
                            title={wizardStateStore.currentStepTitle}
                            description={wizardStateStore.currentStepDescription}
                        />

                        <DialogWizardStepContent key={wizardStateStore.currentStepId}>
                            {wizardStateStore.currentStepElement}
                        </DialogWizardStepContent>
                    </>
                )}
                footer={(
                    <DialogWizardActions
                        prev={{
                            disabled: wizardStateStore.isFirstStep,
                            didSelect: () => wizardStateStore.goToPrevStep(),
                            text: wizardStateStore.currentPrevActionText,
                        }}
                        next={{
                            disabled: wizardStateStore.isLastStep,
                            didSelect: () => wizardStateStore.goToNextStep(),
                            text: wizardStateStore.currentNextActionText,
                        }}
                        skip={{ disabled: true }}
                        done={{
                            disabled: !wizardStateStore.isEdit && !wizardStateStore.isLastStep,
                            didSelect: () => wizardStateStore.finish(hideWizardActon),
                            text: wizardStateStore.currentDoneActionText,
                        }}
                        cancel={{
                            disabled: false,
                            didSelect: () => dialogActions.activate(),
                            text: wizardStateStore.currentCancelActionText,
                        }}
                    />
                )}
                onRequestClose={() => dialogActions.activate()}
                size={props.size}
                pending={wizardStateStore.pending}
                loading={wizardStateStore.loading}
            />

            {dialog}
        </WizardDataContext.Provider>
    );
});
